import Modal from 'react-modal';
import React, { useEffect, useState } from 'react';
import styles from './AddExtraIncomeModal.module.css';
import { fetchWithFormData } from '../util/fetchWithFormData';
import { toast } from 'react-toastify';

type UploadSupportingDocumentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: number;
};

type CategoryOption = {
  value: string;
  label: string;
};

const CATEGORIES: CategoryOption[] = [
  { value: 'quote', label: 'Quote' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'proof_of_payment', label: 'Proof of Payment' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'other', label: 'Other' }
];

const UploadSupportingDocumentModal: React.FC<UploadSupportingDocumentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  orderId,
}) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setSelectedCategory('');
    setSelectedFile(null);
    setErrors({});
    setDragActive(false);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedCategory) {
      newErrors.category = 'Please select a category';
    }

    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload';
    } else {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(selectedFile.type)) {
        newErrors.file = 'File must be a PDF or image (PNG, JPG)';
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (selectedFile.size > maxSize) {
        newErrors.file = 'File size must be less than 10MB';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file);
    if (file && errors.file) {
      // Clear file error when new file is selected
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!validateForm()) {
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('category', selectedCategory);
      formData.append('file', selectedFile!);

      const response = await fetchWithFormData(
        `/orders/${orderId}/supporting_documents`,
        formData
      );

      if (response.ok) {
        toast.success('Document uploaded successfully');
        onSuccess();
        onClose();
      } else {
        const errorData = await response.json();
        toast.error(errorData.errors?.[0] || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('An error occurred while uploading');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modalContent}>
      <h2 className="text-2xl font-bold mb-4">Upload Supporting Document</h2>

      <div className="space-y-4">
        {/* Category Selection */}
        <div>
          <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 mb-1">
            Document Category *
          </label>
          <select
            id="category-select"
            data-testid="category-select"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              if (errors.category) setErrors(prev => ({ ...prev, category: '' }));
            }}
            className="w-full px-3 py-2 border rounded"
          >
            <option value="">Select a category...</option>
            {CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className="text-red-500 text-sm mt-1">{errors.category}</p>
          )}
        </div>

        {/* File Upload - Drag & Drop */}
        <div>
          <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700 mb-1">
            File * (PDF or Image, max 10MB)
          </label>
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-md p-6 text-center ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
          >
            {selectedFile ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <button
                  onClick={() => handleFileSelect(null)}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Drag and drop a file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-input"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-block px-4 py-2 bg-gray-200 text-gray-800 rounded cursor-pointer hover:bg-gray-300"
                >
                  Browse Files
                </label>
              </div>
            )}
          </div>
          {errors.file && (
            <p className="text-red-500 text-sm mt-1">{errors.file}</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={onClose}
          disabled={isUploading}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleUpload}
          disabled={isUploading}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
    </Modal>
  );
};

export default UploadSupportingDocumentModal;
